/**
 * cyrb128 hash function - Convert string to 128-bit seed
 *
 * Hash functions generate very different results even for similar inputs.
 * This is used to convert string seeds (like "2025-01-24") into numeric
 * seeds suitable for the sfc32 PRNG.
 *
 * @param str - Input string (e.g., "2025-01-24" for daily challenge)
 * @returns Array of four 32-bit integers for sfc32
 *
 * @example
 * ```ts
 * const seeds = cyrb128("2025-01-24");
 * const rng = sfc32(seeds[0], seeds[1], seeds[2], seeds[3]);
 * ```
 */
export function cyrb128(str: string): [number, number, number, number] {
  let h1 = 1779033703,
    h2 = 3144134277,
    h3 = 1013904242,
    h4 = 2773480762;

  for (let i = 0, k; i < str.length; i++) {
    k = str.charCodeAt(i);
    h1 = h2 ^ Math.imul(h1 ^ k, 597399067);
    h2 = h3 ^ Math.imul(h2 ^ k, 2869860233);
    h3 = h4 ^ Math.imul(h3 ^ k, 951274213);
    h4 = h1 ^ Math.imul(h4 ^ k, 2716044179);
  }

  h1 = Math.imul(h3 ^ (h1 >>> 18), 597399067);
  h2 = Math.imul(h4 ^ (h2 >>> 22), 2869860233);
  h3 = Math.imul(h1 ^ (h3 >>> 17), 951274213);
  h4 = Math.imul(h2 ^ (h4 >>> 19), 2716044179);

  return [
    (h1 ^ h2 ^ h3 ^ h4) >>> 0,
    (h2 ^ h1) >>> 0,
    (h3 ^ h1) >>> 0,
    (h4 ^ h1) >>> 0,
  ];
}

/**
 * sfc32 (Simple Fast Counter) PRNG - 128-bit state for higher entropy
 *
 * Highly rated by PractRand testing suite. This is a high-quality PRNG
 * that passes stringent statistical tests while being fast and having
 * a small state size. Best for complex procedural generation.
 *
 * @param a - First 32-bit integer seed component
 * @param b - Second 32-bit integer seed component
 * @param c - Third 32-bit integer seed component
 * @param d - Fourth 32-bit integer seed component (counter)
 * @returns Random number generator function that returns values in [0, 1)
 *
 * @example
 * ```ts
 * const rng = sfc32(1234, 5678, 9012, 3456);
 * console.log(rng()); // 0.123456...
 * console.log(rng()); // 0.789012...
 * ```
 */
export function sfc32(
  a: number,
  b: number,
  c: number,
  d: number
): () => number {
  return function () {
    a >>>= 0;
    b >>>= 0;
    c >>>= 0;
    d >>>= 0;
    let t = (a + b) | 0;
    a = b ^ (b >>> 9);
    b = (c + (c << 3)) | 0;
    c = (c << 21) | (c >>> 11);
    d = (d + 1) | 0;
    t = (t + d) | 0;
    c = (c + t) | 0;
    return (t >>> 0) / 4294967296;
  };
}
